import React, { useCallback, useEffect, useState } from 'react';
import {
  Building2, ExternalLink, Loader2, CheckCircle2, Clock, AlertTriangle, ShieldCheck, Wallet,
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../App';
import { Button } from '../ui/button';
import { stripeErrorToast } from '../../utils/stripeErrors';

const STATUS_META = {
  not_connected: {
    tone: 'border-amber-200 bg-amber-50/60',
    badge: 'bg-amber-100 text-amber-700',
    badgeLabel: 'Not connected',
    icon: AlertTriangle,
    heading: 'Add your bank account now so you can get paid faster',
    body: 'Stripe handles all verification, you\'ll just need your bank details, DOB and ID. Takes about 2 minutes. You can skip this for now and do it later from your Earnings tab, but your first payout won\'t go out until this is complete.',
    cta: 'Connect with Stripe',
  },
  action_required: {
    tone: 'border-red-200 bg-red-50/60',
    badge: 'bg-red-100 text-red-700',
    badgeLabel: 'Action required',
    icon: AlertTriangle,
    heading: 'Finish your Stripe onboarding',
    body: 'Stripe needs a few more details before they can pay you out. Pick up where you left off.',
    cta: 'Continue onboarding',
  },
  pending: {
    tone: 'border-amber-200 bg-amber-50/60',
    badge: 'bg-amber-100 text-amber-700',
    badgeLabel: 'Pending verification',
    icon: Clock,
    heading: "Stripe is reviewing your details",
    body: 'This usually takes 1–2 working days. You can continue and submit your profile, Stripe will finish verifying in the background, and your first payout will go out automatically once approved.',
    cta: null,
  },
  enabled: {
    tone: 'border-emerald-200 bg-emerald-50/60',
    badge: 'bg-emerald-100 text-emerald-700',
    badgeLabel: 'Enabled',
    icon: CheckCircle2,
    heading: 'Ready to receive payouts',
    body: 'Your Stripe account is fully verified. The moment your first booking clears, funds start flowing to your bank every Monday.',
    cta: null,
  },
};

function Benefit({ icon: Icon, title, body }) {
  return (
    <div className="flex items-start gap-2">
      <div className="w-6 h-6 rounded-full bg-[#D4AF37]/10 flex items-center justify-center shrink-0 mt-0.5">
        <Icon className="h-3 w-3 text-[#D4AF37]" />
      </div>
      <div>
        <p className="text-sm font-medium text-[#1A1A1A]">{title}</p>
        <p className="text-xs text-stone-500">{body}</p>
      </div>
    </div>
  );
}

// Auto-onboarding step, sits between Services (5) and Review (7). Optional:
// providers can skip and connect later from /provider/dashboard?tab=earnings,
// but we heavily encourage completing it here so their first payout is instant.
export function PayoutStep({ onStatusChange }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [justReturned, setJustReturned] = useState(false);

  const load = useCallback(async (refresh = false) => {
    try {
      const res = await api.get('/providers/me/stripe-connect/status', {
        params: refresh ? { refresh: true } : {},
      });
      setStatus(res.data);
      onStatusChange?.(res.data);
    } catch (err) {
      // Provider profile might not yet exist, treat as not_connected.
      const fallback = {
        connected: false, status: 'not_connected',
        charges_enabled: false, payouts_enabled: false,
        details_submitted: false, requirements_due: [],
      };
      setStatus(fallback);
      onStatusChange?.(fallback);
    } finally { setLoading(false); }
  }, [onStatusChange]);

  useEffect(() => {
    (async () => {
      await load(false);
      await load(true);
    })();
  }, [load]);

  useEffect(() => {
    // Detect return from Stripe hosted onboarding.
    const params = new URLSearchParams(window.location.search);
    const onboarding = params.get('stripe_onboarding');
    if (onboarding === 'complete') {
      setJustReturned(true);
      toast.success('Welcome back! Checking your Stripe status...');
      load(true);
    } else if (onboarding === 'refresh') {
      toast.info('Link expired, re-issuing a fresh one.');
    }
    if (onboarding) {
      params.delete('stripe_onboarding');
      const newSearch = params.toString();
      window.history.replaceState(
        {}, '', window.location.pathname + (newSearch ? `?${newSearch}` : '')
      );
    }
  }, [load]);

  const handleConnect = async () => {
    setStarting(true);
    try {
      const res = await api.post('/providers/me/stripe-connect/onboard', null, {
        params: { origin: 'onboarding' },
      });
      if (res.data?.url) { window.location.href = res.data.url; return; }
      toast.error('Could not generate onboarding link');
    } catch (err) {
      toast.error(stripeErrorToast(err, 'Could not start onboarding'));
    } finally {
      setStarting(false);
    }
  };

  if (loading && !status) {
    return (
      <div className="flex items-center justify-center py-10" data-testid="payout-step-loading">
        <Loader2 className="h-6 w-6 animate-spin text-stone-400" />
      </div>
    );
  }

  const state = status?.status || 'not_connected';
  const meta = STATUS_META[state];
  const Icon = meta.icon;

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="payout-step">
      <div>
        <h2 className="font-heading text-2xl font-medium text-[#1A1A1A] mb-2">
          Get ready for payouts
        </h2>
        <p className="text-stone-500">
          Connecting your bank account now means your first payment lands in your account within days, not weeks.
          Stripe handles all the KYC, we never see your bank details.
        </p>
      </div>

      {/* Hero benefit strip */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Benefit icon={Wallet} title="Paid every Monday" body="Automatic weekly transfers once your balance hits £100." />
        <Benefit icon={ShieldCheck} title="Secure by Stripe" body="Full KYC + payouts enabled on GBP bank accounts." />
        <Benefit icon={Clock} title="48h clearance" body="Funds clear 48h after service completion, built-in dispute protection." />
      </div>

      {/* Status card */}
      <div
        className={`rounded-2xl border ${meta.tone} p-5`}
        data-testid="payout-step-status-card"
        data-status={state}
      >
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-full bg-white border border-[#E6E4DD] flex items-center justify-center shrink-0">
            <Building2 className="h-5 w-5 text-[#1A1A1A]" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <h3 className="font-heading text-lg font-medium text-[#1A1A1A]">Stripe Connect</h3>
              <span
                className={`inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full ${meta.badge}`}
                data-testid="payout-step-status-badge"
              >
                <Icon className="h-3 w-3" />
                {meta.badgeLabel}
              </span>
            </div>
            <p className="text-sm font-medium text-[#1A1A1A]">{meta.heading}</p>
            <p className="text-sm text-stone-600 mt-1">{meta.body}</p>

            {meta.cta && (
              <Button
                onClick={handleConnect}
                disabled={starting}
                className="bg-[#D4AF37] hover:bg-[#C49E2F] text-white gap-2 mt-4"
                data-testid="payout-step-connect-btn"
              >
                {starting ? <Loader2 className="h-4 w-4 animate-spin" /> : <ExternalLink className="h-4 w-4" />}
                {starting ? 'Opening Stripe...' : meta.cta}
              </Button>
            )}

            {state === 'pending' && justReturned && (
              <p className="text-xs text-stone-500 mt-3 flex items-center gap-1">
                <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                Details submitted. Feel free to continue, we'll finish verification in the background.
              </p>
            )}
          </div>
        </div>
      </div>

      {state !== 'enabled' && state !== 'pending' && (
        <div className="rounded-lg bg-[#F9F6F0] border border-[#E6E4DD] px-4 py-3">
          <p className="text-xs text-stone-500 flex items-start gap-2">
            <AlertTriangle className="h-3.5 w-3.5 text-amber-500 shrink-0 mt-0.5" />
            <span>
              This step is optional, you can skip and connect later from your Earnings tab. But new stylists
              who connect during onboarding get their first payout in under 48 hours instead of 7–10 days.
            </span>
          </p>
        </div>
      )}
    </div>
  );
}
